import { useWorkPropertyVisibleStore } from '../../store/workPropertyVisibleStore';
import { useWorkListStore } from '../../store/WorkListStore';
import { getWorkPropertyVisible, updateWorkPropertyVisible } from '../../api/workPropertyVisibleApi';
import { WorkPropertyVisibleData } from '../../dto/EntityRepository';
import { WorkPropertyVisibleResponse } from '../../dto/ResDtoRepository';
import { UpdateWorkPropertyVisibleReq } from '../../dto/ReqDtoRepository';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { useApiWrapper } from './apiWrapper';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { normalizeWorkPropertyVisible } from '../../util/normalizeWorkData';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';

interface UseWorkPropertyVisibleResult {
  workPropertyVisible: WorkPropertyVisibleData | null;
  getWorkPropertyVisible: () => Promise<void>;
  updateWorkPropertyVisible: (data: UpdateWorkPropertyVisibleReq) => Promise<void>;
  updateEngVisible: (data: UpdateWorkPropertyVisibleReq) => Promise<void>;
}

export const useWorkPropertyVisible = (): UseWorkPropertyVisibleResult => {
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const {
    workPropertyVisible,
    setWorkPropertyVisible,
    setWorkPropertyVisibleEngVisibleAfterUpdate,
  } = useWorkPropertyVisibleStore();
  const setWorkListType = useWorkListStore((s) => s.setWorkListType);
  const { setUpdatedTempAlert } = useTempAlertStore();
  const withApiHandler = useApiWrapper();

  const handleSuccess = (response: WorkPropertyVisibleResponse) => {
    const data = normalizeWorkPropertyVisible(response.data);
    setWorkPropertyVisible(data);
    if (data.workListType) {
      setWorkListType(data.workListType);
    }
  };
  const handleUpdateSuccess = (response: WorkPropertyVisibleResponse) => {
    setWorkPropertyVisible(normalizeWorkPropertyVisible(response.data));
    setUpdatedTempAlert();
  };

  const handleUpdateEngVisibleSuccess = (response: WorkPropertyVisibleResponse) => {
    setWorkPropertyVisibleEngVisibleAfterUpdate(normalizeWorkPropertyVisible(response.data));
    setUpdatedTempAlert();
  };

  const handleWorkPropertyVisibleRequest = async (
    action: 'get' | 'update' | 'updateEngVisible',
    data?: UpdateWorkPropertyVisibleReq
  ) => {
    if (!pageOwner) return;
    const { aui } = pageOwner;
    const apiFunction = async () => {
      switch (action) {
        case 'update':
          return runCurrentArchiveRequest(
            aui,
            () => updateWorkPropertyVisible(aui, data as UpdateWorkPropertyVisibleReq),
            handleUpdateSuccess,
          );
        case 'updateEngVisible':
          return runCurrentArchiveRequest(
            aui,
            () => updateWorkPropertyVisible(aui, data as UpdateWorkPropertyVisibleReq),
            handleUpdateEngVisibleSuccess,
          );
        case 'get':
        default:
          return runCurrentArchiveRequest(
            aui,
            () => getWorkPropertyVisible(aui),
            handleSuccess,
            { matchOwnerAccess: false },
          );
      }
    };

    await withApiHandler(apiFunction, [aui, action, data]);
  };


  const getWorkPropertyVisibleHandler = () => handleWorkPropertyVisibleRequest('get');
  const updateWorkPropertyVisibleHandler = (data: UpdateWorkPropertyVisibleReq) => handleWorkPropertyVisibleRequest('update', data);
  const updateEngVisibleHandler = (data: UpdateWorkPropertyVisibleReq) => handleWorkPropertyVisibleRequest('updateEngVisible', data);

  return {
    workPropertyVisible,
    getWorkPropertyVisible: getWorkPropertyVisibleHandler,
    updateWorkPropertyVisible: updateWorkPropertyVisibleHandler,
    updateEngVisible: updateEngVisibleHandler,
  };
};
